import { ReactNode, useState } from 'react';
import { Clock, FileText } from 'lucide-react';
import axios from '@lambda/commons/apis/axios';
import { CommonHelpers } from '@lambda/reebelo';
import { CtaButton } from '@/components/Cta/Cta';
import { backendBaseUrl } from '@/lib/smartForm/types';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import settings from '@/settings/index';
import i18n from '@/settings/i18n';

const t = i18n.init('orders');

// Flat, page-shaped props rather than an order object: the legacy Orders page
// and the revamped one hold the same order in two different shapes.
interface RequestInvoiceCtaProps {
  orderId: string;
  orderName: string;
  orderNumber: string;
  shopifyOrderId: string;
  processedAt: string;
  vendorName: string;
  lineItems: Array<{ id: string | number; name: string; vendor: string }>;
  productNames: string[];
  hasPendingInvoiceRequest?: boolean;
}

type RequestStatus = 'idle' | 'loading' | 'sent' | 'error';

/**
 * Plain text on desktop, a full-width button on mobile — the same split the
 * neighbouring DownloadInvoices uses.
 */
const Row = ({
  icon,
  label,
  disabled,
  loading,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
}) => (
  <div className="w-full sm:w-auto">
    <div className="hidden sm:block">
      <button
        type="button"
        className="flex items-center gap-1.5 text-sm font-bold text-gray-700 disabled:opacity-70"
        disabled={disabled}
        onClick={onClick}
      >
        {icon}
        {label}
      </button>
    </div>
    <div className="block sm:hidden">
      <CtaButton
        variant="outlined"
        disabled={disabled}
        loading={loading}
        className="flex w-full items-center justify-center gap-1.5 p-3 text-sm font-bold"
        onClick={onClick}
      >
        {icon}
        {label}
      </CtaButton>
    </div>
  </div>
);

export default function RequestInvoiceCta({
  orderId,
  orderName,
  orderNumber,
  shopifyOrderId,
  processedAt,
  vendorName,
  lineItems,
  productNames,
  hasPendingInvoiceRequest,
}: RequestInvoiceCtaProps) {
  const { store } = settings;
  const { customer } = useCustomerContext();
  const [status, setStatus] = useState<RequestStatus>('idle');

  if (!CommonHelpers.isSellerInvoiceStore(store) || !vendorName) {
    return null;
  }

  const onClickRequestInvoice = async () => {
    setStatus('loading');

    try {
      // Ask the seller first — this is what puts the order on Cobalt's Invoice
      // request tab and starts the upload SLA. Idempotent per order and seller,
      // so a repeat click is harmless.
      await axios.post('/api/accounts/orders/invoices/requests', {
        orderId,
        vendorName,
      });

      const { data } = await axios.get<{ token: string }>(
        '/api/accounts/zendesk/tokens',
      );

      if (data?.token == null) {
        throw Error('Missing Zendesk token');
      }

      await axios.post(
        `${backendBaseUrl}/smartforms/tickets`,
        {
          type: 'help',
          order: {
            orderName,
            orderNumber,
            email: customer?.email ?? '',
            firstName: customer?.firstName ?? '',
            lastName: customer?.lastName ?? '',
            processedAt,
            shipments: [
              {
                lineItems: lineItems.map((li) => ({
                  name: li.name,
                  vendor: li.vendor,
                  id: li.id,
                })),
              },
            ],
            // Orders that never existed in Shopify have no shopifyId, and the
            // smartforms schema requires this field, so callers fall back to the
            // MsOrder id to keep the ticket createable.
            shopifyOrderId,
          },
          params: {
            selectedHelpOption: 'HPC-3',
            vendorName,
            productName: productNames.filter(Boolean).join(', '),
            textData:
              'Customer requested a copy of their invoice for this order via the "Request invoice" button.',
          },
          attachments: [],
          store,
        },
        { headers: { Authorization: `Bearer ${data.token}` } },
      );

      setStatus('sent');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to submit invoice request', err);
      setStatus('error');
    }
  };

  if (hasPendingInvoiceRequest === true || status === 'sent') {
    return (
      <Row
        icon={<Clock className="h-4 w-4" strokeWidth={2} />}
        label={t`Invoice requested`}
        disabled
      />
    );
  }

  return (
    <>
      <Row
        icon={<FileText className="h-4 w-4" strokeWidth={2} />}
        label={status === 'loading' ? t`Requesting...` : t`Request invoice`}
        disabled={status === 'loading'}
        loading={status === 'loading'}
        onClick={onClickRequestInvoice}
      />
      {status === 'error' && (
        <p className="mt-1 text-sm text-red">
          {t`Something went wrong, please try again.`}
        </p>
      )}
    </>
  );
}
